import { useEffect, useRef, useState, ChangeEvent, ClipboardEvent, KeyboardEvent } from 'react'
import styles from './PinInput.module.scss'

interface PinInputProps {
   /** Valeur contrôlée (chaîne de 0 à `length` chiffres). Mettre '' pour réinitialiser. */
   value: string
   onChange: (v: string) => void
   length?: number
   disabled?: boolean
   title?: string
   /** Appelé quand les `length` cases sont remplies. */
   onComplete?: (v: string) => void
}

function padSlots(value: string, length: number): string[] {
   const arr = (value ?? '').replace(/\D/g, '').slice(0, length).split('')
   while (arr.length < length) arr.push('')
   return arr
}

/**
 * Saisie d'un code PIN en `length` cases carrées.
 * - saisie d'un chiffre → focus case suivante
 * - Backspace : vide la case courante, sinon vide + focus la précédente
 * - collage : découpe le presse-papier, ne garde que les chiffres, répartit sur les cases
 * - chiffres uniquement
 * Les cases (positions) sont la source d'affichage ; `onChange` remonte la concaténation.
 */
function PinInput({ value, onChange, length = 6, disabled, title, onComplete }: PinInputProps) {
   const refs = useRef<Array<HTMLInputElement | null>>([])
   const [slots, setSlots] = useState<string[]>(() => padSlots(value, length))

   // Réinitialisation depuis le parent (ex. après changement de mot de passe réussi).
   useEffect(() => {
      if (value === '') setSlots(Array(length).fill(''))
   }, [value, length])

   const emit = (next: string[]) => {
      setSlots(next)
      onChange(next.join(''))
      if (next.every((c) => c !== '')) onComplete?.(next.join(''))
   }

   const focusBox = (i: number) => {
      const idx = Math.max(0, Math.min(length - 1, i))
      const el = refs.current[idx]
      el?.focus()
      el?.select()
   }

   const handleChange = (idx: number, e: ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/\D/g, '')
      const next = [...slots]
      if (digits === '') {
         next[idx] = ''
         emit(next)
         return
      }
      next[idx] = digits.slice(-1) // dernier chiffre saisi
      emit(next)
      if (idx < length - 1) focusBox(idx + 1)
   }

   const handleKeyDown = (idx: number, e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
         e.preventDefault()
         const next = [...slots]
         if (next[idx] !== '') {
            next[idx] = ''
            emit(next)
         } else if (idx > 0) {
            next[idx - 1] = ''
            emit(next)
            focusBox(idx - 1)
         }
      } else if (e.key === 'ArrowLeft') {
         e.preventDefault()
         focusBox(idx - 1)
      } else if (e.key === 'ArrowRight') {
         e.preventDefault()
         focusBox(idx + 1)
      }
   }

   const handlePaste = (idx: number, e: ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault()
      const digits = (e.clipboardData.getData('text') ?? '').replace(/\D/g, '')
      if (!digits) return
      const next = [...slots]
      for (let i = 0; i < length - idx && i < digits.length; i++) {
         next[idx + i] = digits[i]
      }
      emit(next)
      const filled = Math.min(length - 1, idx + digits.length)
      focusBox(filled)
   }

   return (
      <div className={styles.container}>
         {title && <label className={styles.title}>{title}</label>}
         <div className={styles.boxes}>
            {Array.from({ length }).map((_, idx) => (
               <input
                  key={idx}
                  ref={(el) => (refs.current[idx] = el)}
                  className={styles.box}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  autoComplete="off"
                  disabled={disabled}
                  value={slots[idx] ?? ''}
                  onChange={(e) => handleChange(idx, e)}
                  onKeyDown={(e) => handleKeyDown(idx, e)}
                  onPaste={(e) => handlePaste(idx, e)}
                  onFocus={(e) => e.target.select()}
               />
            ))}
         </div>
      </div>
   )
}

export default PinInput
